import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useDictionary } from '@/lib/i18n'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

import { BIKE_IMAGES } from '@/constants/bike'

const CraftSection: FC = (): ReactNode => {
    const t = useDictionary()
    const { craft } = t
    const seatTube = BIKE_IMAGES.seatTube
    const cockpit = BIKE_IMAGES.cockpit

    return (
        <section id='craft' aria-labelledby='craft-title' className='relative overflow-hidden py-24 sm:py-32'>
            <div aria-hidden='true' className='pointer-events-none absolute -left-48 top-10 size-[40rem] rounded-full bg-race/15 blur-[140px]' />

            <div className='relative mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='grid items-center gap-12 lg:grid-cols-[1.3fr_1fr] lg:gap-16'>
                    <Reveal>
                        <figure className='relative overflow-hidden rounded-3xl border border-border bg-[radial-gradient(ellipse_at_62%_45%,oklch(0.3_0.1_25),oklch(0.16_0.008_286)_72%)]'>
                            <Image
                                src={seatTube.src}
                                width={seatTube.width}
                                height={seatTube.height}
                                alt={craft.mainImageAlt}
                                sizes='(min-width: 1280px) 700px, (min-width: 1024px) 55vw, 100vw'
                                className='h-auto w-full'
                            />

                            <figcaption className='absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/65 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-vapor backdrop-blur'>
                                <span aria-hidden='true' className='size-1.5 rounded-full bg-race-bright' />
                                {craft.caption}
                            </figcaption>
                        </figure>
                    </Reveal>

                    <div>
                        <Reveal>
                            <SectionHeading id='craft-title' eyebrow={craft.eyebrow} title={craft.title} />
                        </Reveal>

                        <div className='mt-8 space-y-5 text-base leading-relaxed text-foreground/80 sm:text-lg'>
                            {craft.paragraphs.map((paragraph, index) => (
                                <Reveal key={paragraph} delay={index * 80}>
                                    <p className='text-pretty'>
                                        {paragraph}
                                    </p>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>

                <div className='mt-16 grid gap-5 lg:mt-24 lg:grid-cols-[1fr_1.15fr]'>
                    <ol className='order-2 grid gap-5 lg:order-1'>
                        {craft.details.map((detail, index) => (
                            <li key={detail.title}>
                                <Reveal delay={index * 100} className='h-full'>
                                    <article className='h-full rounded-2xl border border-border bg-card/70 p-6 transition-colors duration-300 hover:border-race/40 sm:p-7'>
                                        <span className='font-mono text-xs text-race-bright'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>

                                        <h3 className='display-heading mt-3 font-heading text-2xl font-semibold uppercase tracking-tight text-foreground'>
                                            {detail.title}
                                        </h3>

                                        <p className='mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base'>
                                            {detail.body}
                                        </p>
                                    </article>
                                </Reveal>
                            </li>
                        ))}
                    </ol>

                    <Reveal className='order-1 lg:order-2 lg:h-full'>
                        <figure className='relative aspect-square overflow-hidden rounded-3xl border border-border bg-[#efefef] sm:aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[28rem]'>
                            <Image
                                src={cockpit.src}
                                fill
                                alt={craft.detailImageAlt}
                                sizes='(min-width: 1280px) 680px, (min-width: 1024px) 52vw, 100vw'
                                className='object-cover'
                            />

                            <figcaption className='absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-black/75 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-vapor'>
                                <span aria-hidden='true' className='size-1.5 rounded-full bg-race-bright' />
                                {craft.detailImageCaption}
                            </figcaption>
                        </figure>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}

export default CraftSection
